import { createSelector, createSlice, PayloadAction } from '@reduxjs/toolkit';
import arrayMove from 'array-move';

import {
  AddPollAction,
  AddRouletteAction,
  AttachBodyItem,
  AttachState,
  ClearAction,
  InitFromAttachmentsAction,
  LoadLinkAction,
  PushPhotosAction,
  RemoveLinkAction,
  RemovePhotoAction,
  RemovePollAction,
  SaveUploadedVideoAction,
  SetIsAdditionalModalOpenedAction,
  SetLinkAction,
  SetLoadedLinkAction,
  SetTimerAction,
  SetUploadingVideoAction,
  SetUploadingVideoIdAction,
  SetUploadingVideoProgressAction,
  SetUploadingVideoStatusAction,
  SetUploadingVideoUploadData,
  SwapPhotosAction,
  UpdatePhotoAction,
  UploadPhotosAction,
  UploadVideoAction,
} from './interface';
import { RootState } from 'store/rootReducer';
import { AttachItem, AttachPhoto, AttachType } from '$store/models';

const uploadingVideoDataInitial = {
  video: null,
  loaded: 0,
  total: 0,
  isLoaded: false,
  isCancelled: false,
  pickerId: '',
};

const initialState: AttachState = {
  photos: {},
  links: {},
  deleteLinks: {},
  linkLoading: {},
  timers: {},
  linkFailed: {},
  isAdditionalModalOpened: false,
  uploadingVideoData: uploadingVideoDataInitial,
  pollId: {},
  rouletteId: {},
};

export const { actions, reducer } = createSlice({
  name: 'attach',
  initialState,
  reducers: {
    uploadPhotos(_, action: UploadPhotosAction) {},
    uploadVideo(_, action: UploadVideoAction) {},
    pushPhotos(state, action: PushPhotosAction) {
      const { pickerId, photos } = action.payload;

      state.photos[pickerId] = [...(state.photos[pickerId] || []), ...photos];
    },

    removePhoto(state, action: RemovePhotoAction) {
      const { pickerId, photoId } = action.payload;

      if (state.photos[pickerId]) {
        let newPhotos = [...state.photos[pickerId]];
        for (let i = 0; i < newPhotos.length; i++) {
          if (newPhotos[i].photoId === photoId) {
            newPhotos.splice(i, 1);
            break;
          }
        }
        state.photos[pickerId] = newPhotos;
      }
    },
    updatePhoto(state, action: UpdatePhotoAction) {
      const { pickerId, photoId, updated } = action.payload;

      if (state.photos[pickerId]) {
        let newPhotos = [...state.photos[pickerId]];
        for (let i = 0; i < newPhotos.length; i++) {
          if (newPhotos[i].photoId === photoId) {
            newPhotos[i] = {
              ...newPhotos[i],
              ...updated,
            };
            break;
          }
        }
        state.photos[pickerId] = newPhotos;
      }
    },
    swapPhotos(state, action: SwapPhotosAction) {
      const { pickerId, oldIndex, newIndex } = action.payload;
      const photos = state.photos[pickerId];

      state.photos[pickerId] = arrayMove(photos, oldIndex, newIndex);
    },
    clear(state, action: ClearAction) {
      if (!state.isAdditionalModalOpened) {
        delete state.photos[action.payload];
        delete state.links[action.payload];
        delete state.timers[action.payload];
        delete state.pollId[action.payload];
        delete state.rouletteId[action.payload];
      }
    },
    initFromAttachments(state, action: InitFromAttachmentsAction) {
      const { pickerId, attachments } = action.payload;

      state.photos[pickerId] = attachments
        .filter((item) => [AttachType.PHOTO, AttachType.VIDEO].indexOf(item.type) > -1)
        .map((item: AttachItem) => {
          if (item.type === AttachType.PHOTO) {
            const photo = item.photo!;
            return {
              photoId: photo.photoId,
              photoMedium: photo.photoMedium,
              photoLarge: photo.photoLarge,
              photoMediumEncrypted: photo.photoMediumEncrypted,
              photoLargeEncrypted: photo.photoLargeEncrypted,
              thumbnail: photo.thumbnail,
              width: photo.width,
              height: photo.height,
            };
          } else {
            const video = item.video!;
            return {
              isVideo: true,
              photoId: video.videoId,
              photoMedium: video.photoMedium,
              photoLarge: video.photoLarge,
              width: video.width,
              height: video.height,
            };
          }
        });

      state.links[pickerId] = attachments
        .filter((item) => item.type === AttachType.LINK)
        .map((item) => item.link!)[0];

      const poll = attachments
        .filter((item) => item.type === AttachType.POLL)
        .map((item) => item.poll!)[0];

      if (poll) {
        state.pollId[pickerId] = poll.pollId;
      }

      const roulette = attachments
        .filter((item) => item.type === AttachType.ROULETTE)
        .map((item) => item.roulette!)[0];

      if (roulette) {
        state.rouletteId[pickerId] = roulette.rouletteId;
      }
    },
    loadLink(state, action: LoadLinkAction) {
      const { pickerId } = action.payload;

      if (!state.links[pickerId]) {
        state.linkLoading[pickerId] = true;
      }
    },
    setLink(state, action: SetLinkAction) {
      const { pickerId, link } = action.payload;

      state.links[pickerId] = link;
    },
    setLoadedLink(state, action: SetLoadedLinkAction) {
      delete state.linkLoading[action.payload];
    },
    setLinkFailed(state, action: SetLoadedLinkAction) {
      state.linkFailed[action.payload] = true;
    },
    removeLink(state, action: RemoveLinkAction) {
      delete state.links[action.payload];
      state.deleteLinks[action.payload] = true;
    },
    setTimer(state, action: SetTimerAction) {
      const { pickerId, time } = action.payload;

      if (time) {
        state.timers[pickerId] = time;
      } else {
        delete state.timers[pickerId];
      }
    },
    setUploadingVideo(state, action: SetUploadingVideoAction) {
      state.uploadingVideoData = action.payload;
    },
    setUploadingVideoProgress(state, action: SetUploadingVideoProgressAction) {
      if ('loaded' in action.payload) {
        const { loaded, total } = action.payload;

        state.uploadingVideoData.loaded = loaded;
        state.uploadingVideoData.total = total;
        return;
      }

      const { isLoaded } = action.payload;
      state.uploadingVideoData.isLoaded = isLoaded;
    },
    setUploadingVideoStatus(state, action: SetUploadingVideoStatusAction) {
      const { status, attempt, error } = action.payload;
      state.uploadingVideoData.status = status;
      state.uploadingVideoData.attempt = attempt;
      state.uploadingVideoData.error = error;
    },
    setUploadingVideoId(state, action: SetUploadingVideoIdAction) {
      state.uploadingVideoData.uploadId = action.payload;
    },
    retryUploadingVideo() {},
    retrySavingVideo() {},
    cancelUploadingVideo(state) {
      state.uploadingVideoData.isCancelled = true;
    },
    setUploadingVideoUploadData(state, action: SetUploadingVideoUploadData) {
      state.uploadingVideoData.uploadData = action.payload;
    },
    saveUploadedVideo(state, action: SaveUploadedVideoAction) {},
    clearUploadingVideoData(state) {
      state.uploadingVideoData = uploadingVideoDataInitial;
    },
    setIsAdditionalModalOpened(state, action: SetIsAdditionalModalOpenedAction) {
      state.isAdditionalModalOpened = action.payload;
    },
    addPoll(state, action: AddPollAction) {
      const { pickerId, pollId } = action.payload;
      state.pollId[pickerId] = pollId;
    },
    removePoll(state, action: RemovePollAction) {
      delete state.pollId[action.payload];
    },
    addRoulette(state, action: AddRouletteAction) {
      const { pickerId, rouletteId } = action.payload;
      state.rouletteId[pickerId] = rouletteId;
    },
    removeRoulette(state, action: RemovePollAction) {
      delete state.rouletteId[action.payload];
    },
  },
});

export { reducer as attachReducer, actions as attachActions };

export const attachSelector = (state: RootState) => state.attach;
export const uploadingVideoSelector = (state: RootState) =>
  state.attach.uploadingVideoData;

export const attachPhotosSelector = createSelector(
  [attachSelector, (_, pickerId: string) => pickerId],
  (attach, pickerId) => {
    return attach.photos[pickerId] || [];
  },
);

export const attachPollSelector = createSelector(
  [attachSelector, (_, pickerId: string) => pickerId],
  (attach, pickerId) => {
    return attach.pollId[pickerId] || 0;
  },
);

export const attachRouletteSelector = createSelector(
  [attachSelector, (_, pickerId: string) => pickerId],
  (attach, pickerId) => {
    return attach.rouletteId[pickerId] || 0;
  },
);

export const attachLinksSelector = createSelector(
  [attachSelector, (_, pickerId: string) => pickerId],
  (attach, pickerId) => {
    return attach.links[pickerId];
  },
);

export const attachTimerSelector = createSelector(
  [attachSelector, (_, pickerId: string) => pickerId],
  (attach, pickerId) => {
    return attach.timers[pickerId];
  },
);

export const attachItemsSelector = createSelector(
  [attachSelector, (_, pickerId: string) => pickerId],
  (attach, pickerId) => {
    const photos = attach.photos[pickerId] || [];
    const link = attach.links[pickerId];
    const poll = attach.pollId[pickerId];
    const roulette = attach.rouletteId[pickerId];

    let result: AttachBodyItem[] = [];
    for (let photo of photos) {
      if (photo.photoId < 0) {
        return 'photo_uploading';
      }

      if (photo.isVideo) {
        result.push({
          type: AttachType.VIDEO,
          video: {
            videoId: photo.photoId,
          },
        });
      } else {
        result.push({
          type: AttachType.PHOTO,
          photo: {
            photoId: photo.photoId,
          },
        });
      }
    }

    if (link) {
      result.push({
        type: AttachType.LINK,
        link: `${link.type}${link.object.id}`,
      });
    }

    if (poll) {
      result.push({
        type: AttachType.POLL,
        poll: {
          pollId: poll,
        },
      });
    }

    if (roulette) {
      result.push({
        type: AttachType.ROULETTE,
        roulette: {
          rouletteId: roulette,
        },
      });
    }

    return result;
  },
);

export const attachFullItemsSelector = createSelector(
  [attachSelector, (_, pickerId: string) => pickerId],
  (attach, pickerId) => {
    const photos = attach.photos[pickerId] || [];
    const link = attach.links[pickerId];
    const poll = attach.pollId[pickerId];

    let result: AttachItem[] = [];
    for (let photo of photos) {
      if (photo.isVideo) {
        result.push({
          type: AttachType.VIDEO,
          // @ts-ignore
          video: photo,
        });
      } else {
        result.push({
          type: AttachType.PHOTO,
          photo,
        });
      }
    }

    if (link) {
      result.push({
        type: AttachType.LINK,
        link,
      });
    }

    if (poll) {
      result.push({
        type: AttachType.POLL,
        // @ts-ignore
        poll,
      });
    }

    return result;
  },
);
